import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import "../ContentPages/style.css";
import SubmitButton from "../Buttons/SubmitButton.jsx";
import BackButton from "../Buttons/BackButton.jsx";
import EditButton from "../Buttons/EditButton.jsx";
import CancelButton from "../Buttons/CancelButton.jsx";
import FieldError from "../FieldError/index.jsx";

import { AppContext } from "../../contexts/AppContext.jsx";
import { getLabelFromId } from "../../Services/localStorage.tsx";
import { getCountries } from "../../Services/metadataService.jsx";
import {
  initiateApplication,
  submitApplication,
  updateApplication,
} from "../../Services/applicationService.jsx";
import { calculateAge } from "../../Services/localStorage.tsx";
import Loader from "../Loader/Loader.jsx";
import { useLocation } from "react-router-dom";
import { toTitleCase } from "../../Utils/stringHandler.tsx";
import FloatingDateField from "../FormFields/FloatingDateField.tsx";
import Calendar from "../../assets/images/Calendar.svg";
import FloatingSelectField from "../FormFields/FloatingSelectField.tsx";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
type NameChangeFields = {
  reason: string;
  newGiven: string;
  newFamily: string;
  changeDate: string;
  changeCountry: string;
  marriageGiven?: string;
  marriageFamily?: string;
};

type GenderChangeFields = {
  newGender: string;
  greason: string;
};

type FormData = NameChangeFields & GenderChangeFields;

interface Country {
  dia_countryid: string;
  Name: string;
  Statecode: number;
  "ISO code": string;
}

function decodeNameChange(code?: number) {
  if (code == null) return null;

  // returns { reason, isNZ }
  switch (code) {
    case 100000001:
      return { reason: "Marriage", isNZ: true };
    case 100000004:
      return { reason: "Marriage", isNZ: false };
    case 100000002:
      return { reason: "Civil union", isNZ: true };
    case 100000005:
      return { reason: "Civil union", isNZ: false };
    case 100000000:
      return { reason: "Statutory declaration / deed poll", isNZ: true };
    case 100000003:
      return { reason: "Changed to previous name", isNZ: false };
    case 100000006:
      return { reason: "Other document", isNZ: false }; // "Something else" → safest default to overseas
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export default function RequestChange({ handleNext, handleBack }) {
  const { appState, setAppState, userFlows, setUserFlows } =
    useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState("");
  const [showContinue, setShowContinue] = useState(true);

  const location = useLocation();

  const IVS_ADULT = "dee0075c-6dcd-ef11-b8e9-00224891c497"
  const IVS_CHILD = "fee0075c-6dcd-ef11-b8e9-00224891c497"

  useEffect(() => {
    if (location.pathname.includes("request-change-identity"))
      setShowContinue(true);
    else setShowContinue(false);

    // console.log(location.pathname.includes("request-change-identity"));
  }, [location.pathname]);

  /* ---------- summary info ---------- */
  const applicant = appState?.requestPayload?.applicant;

  const genderId = applicant?.genderId;
  const [genderLabel, setGenderLabel] = useState("-");

  /* ---------- metadata -------------- */
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    const fullName =
      [applicant?.givenNames, applicant?.familyName]
        .filter(Boolean)
        .join(" ") || "-"; // Ensure fullName is set even if applicant is null

    setFullName(fullName);
    setLoading(false);
  }, [applicant]);

  useEffect(() => {
    if (!genderId) return;
    getLabelFromId("GenderReference", genderId).then((lbl) =>
      setGenderLabel(lbl ?? "Unknown")
    );
  }, [genderId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const meta = await getCountries();
        const parsed = JSON.parse(meta.countries || "[]") as Country[];
        let filtered = parsed.filter((c) => c.Statecode === 0);
        let ordered = [
          ...filtered.filter((c) => c.Name === "New Zealand"),
          ...filtered.filter((c) => c.Name !== "New Zealand"),
        ];
        let unique: Country[] = Array.from(
          new Map(ordered.map((c) => [c.dia_countryid, c])).values()
        );
        setCountries(unique);
        const curState = appState?.requestPayload || {};
        if (curState?.supportingInformation?.nameChangeSourceCode) {
          setValue(
            "reason",
            decodeNameChange(
              curState?.supportingInformation?.nameChangeSourceCode
            )?.reason || ""
          );
          setValue("newFamily", applicant?.familyName || "");
          setValue("newGiven", applicant?.givenNames || "");
          setValue("marriageGiven", applicant?.marriageGiven || "");
          setValue("marriageFamily", applicant?.marriageFamily || "");
          setValue(
            "changeDate",
            appState?.requestPayload?.eventTypeSearch?.[1]?.dateOfEvent || ""
          );
          setValue(
            "changeCountry",
            appState?.requestPayload?.eventTypeSearch?.[1]?.countryOfEventId
          );
        }
        if (appState?.requestPayload?.supportingInformation?.genderSourceCode) {
          setValue(
            "newGender",
            appState?.requestPayload?.applicant?.genderId || ""
          );
          setValue(
            "greason",
            appState?.requestPayload?.supportingInformation?.genderSourceCode.toString() ||
              ""
          );
        }
      } catch (err) {
        console.error("Failed to load country list", err);
      }
    };

    fetchCountries();
  }, [appState?.requestPayload, applicant]);

  /* ---------- form hook ------------- */
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,

    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "onTouched",
    shouldUnregister: true,
  });

  /* live-watch values for dynamic hints */
  const countrySelected = watch("changeCountry");
  const greasonSelected = watch("greason");
  const reasonSelected = watch("reason");
  const changeDateSelected =
    new Date(watch("changeDate")).setHours(0, 0, 0, 0) ||
    new Date().setHours(0, 0, 0, 0);
  const showChangeDate = reasonSelected !== "Changed to previous name";
  const showChangeCountry = showChangeDate;

  const givenNameValue = watch("newGiven");
  const surnameValue = watch("newFamily");
  const newGenderValue = watch("newGender");
  const marriageFamilyValue = watch("marriageFamily");
  const marriageGivenValue = watch("marriageGiven");

  // eslint-disable-next-line no-control-regex
  const containsSpecialChars = /[^\x00-\x7F]/g.test(surnameValue);
  // eslint-disable-next-line no-control-regex
  const containsSpecialCharsGivenName = /[^\x00-\x7F]/g.test(givenNameValue);

  // eslint-disable-next-line no-control-regex
  const containsSpecialCharsMarriage = /[^\x00-\x7F]/g.test(
    marriageFamilyValue
  );
  // eslint-disable-next-line no-control-regex
  const containsSpecialCharsMarriageGivenName = /[^\x00-\x7F]/g.test(
    marriageGivenValue
  );

  const NZ_COUNTRY_ID = "18ebb77f-2341-e911-a85e-000d3ae0eb6e";
  const DOC = {
    NZ_BIRTH: "700a9fae-a634-ed11-9db1-0022489412bc",
    BIRTH_LONG: "381eb874-1533-e911-a855-000d3ae09197",
    NZ_MARRIAGE: "7534d85a-a934-ed11-9db1-0022489412bc",
    NZ_CIVIL_UNION: "65e8d260-a934-ed11-9db1-0022489412bc",
    MARRIAGE_CERT: "fd12ac80-1533-e911-a855-000d3ae09197",
    CIVIL_UNION_CERT: "1d79e5b0-3899-e911-a863-000d3a6a0a5e",
    NZ_NAME_CHANGE_NZ_BORN: "b3cbe24e-a934-ed11-9db1-0022489412bc",
    NZ_NAME_CHANGE_OVERSEAS: "7ee2df54-a934-ed11-9db1-0022489412bc",
    NAME_CHANGE_DOCUMENT: "bcbb6982-8a40-e911-a84c-000d3ae0b82e",
    NZ_CITIZENSHIP: "2dad80bb-5756-ed11-9562-002248941c78",
    NZ_TRAVEL_DOCUMENT: "527c00ad-5756-ed11-9562-002248941c78",
    FOREIGN_PASSPORT: "b9294e90-bcd5-ee11-904c-000d3acb9d09",
    OVERSEAS_MARR_CIVIL_UNION: "f70227e9-d41e-ef11-840a-00224895cfa5",
    OVERSEAS_FORMAL_NAME_CHANGE: "56d727d4-d71e-ef11-840a-00224895cfa5",
  } as const;

  const getEventTypeId = (
    reason: string | undefined,
    isEventInNZ: boolean,
    countryOfBirthId?: string
  ) => {
    const bornInNZ = countryOfBirthId === NZ_COUNTRY_ID;
    switch (reason) {
      case "Marriage":
        return isEventInNZ ? DOC.NZ_MARRIAGE : DOC.OVERSEAS_MARR_CIVIL_UNION;

      case "Civil union":
        return isEventInNZ ? DOC.NZ_CIVIL_UNION : DOC.OVERSEAS_MARR_CIVIL_UNION;

      // Name Change reasons:
      case "Statutory declaration / deed poll":
        console.log({ isEventInNZ, bornInNZ });
        if (!isEventInNZ) return DOC.OVERSEAS_FORMAL_NAME_CHANGE; // event not in NZ
        return bornInNZ
          ? DOC.NZ_NAME_CHANGE_NZ_BORN
          : DOC.NZ_NAME_CHANGE_OVERSEAS;

      case "Changed to previous name":
        console.log({ isEventInNZ, bornInNZ });

        // if (!isEventInNZ) return DOC.MARRIAGE_CERT; // event not in NZ
        return bornInNZ ? DOC.NZ_NAME_CHANGE_NZ_BORN : DOC.MARRIAGE_CERT;

      case "Other document":
        return DOC.NAME_CHANGE_DOCUMENT;

      default:
        // Sensible fallback for name-change-like flows:
        if (!isEventInNZ) return DOC.NAME_CHANGE_DOCUMENT;
        return bornInNZ
          ? DOC.NZ_NAME_CHANGE_NZ_BORN
          : DOC.NZ_NAME_CHANGE_OVERSEAS;
    }
  };

  const isOverseas =
    (countrySelected &&
      countrySelected !== "18ebb77f-2341-e911-a85e-000d3ae0eb6e") ||
    reasonSelected === "Other document";
  // const isOverseas =
  //   (countrySelected &&
  //     countrySelected !== "18ebb77f-2341-e911-a85e-000d3ae0eb6e") ||
  //   reasonSelected === "Other document" ||
  //   (changeDateSelected
  //     ? changeDateSelected < new Date("2009-01-01").setHours(0, 0, 0, 0)
  //     : false);

  /* ---------- section toggles -------- */
  const [editingName, setEditingName] = useState(false);
  const [editingGender, setEditingGender] = useState(false);

  /* ---------- submit ----------------- */
  const onSubmit = async (data: FormData) => {
    setLoading(true);

    // console.log("Combined payload:", data);
    //const changeCountry = await getCountryByLabel(data.changeCountry);
    if (!editingName && !editingGender) {
      setLoading(false);
      handleNext();
      return;
    }

    const cutoffDate = new Date("1500-01-01").setHours(0, 0, 0, 0);

    const hasValidChangeDate =
      !!data.changeDate && !isNaN(new Date(data.changeDate));
    const isBeforeCutoff =
      hasValidChangeDate &&
      new Date(data.changeDate).setHours(0, 0, 0, 0) < cutoffDate;
    console.log({ isBeforeCutoff, hasValidChangeDate });
    if (
      data.reason === "Other document" ||
      (data.changeCountry !== "18ebb77f-2341-e911-a85e-000d3ae0eb6e" &&
        data.changeCountry !== "" &&
        data.changeCountry !== undefined) ||
      isBeforeCutoff ||
      ["100000003", "100000002"].includes(data.greason)
    ) {
      console.log(data.reason);
      console.log(data.changeCountry);
      console.log(isBeforeCutoff);
      console.log(["100000003", "100000002"].includes(data.greason));
      console.log("Needs in-person verification");
      setUserFlows((prev: any) => ({
        ...prev,
        showLiveness: false,
        showPartnerStore: true,
        showRefree: false,
        genderChanged: newGenderValue !== genderId,
      }));
    } else if (userFlows.isRenew) {
      setUserFlows((prev: any) => ({
        ...prev,
        showRefree: false,
        genderChanged: newGenderValue !== genderId,
      }));
    }

    const isNZ =
      data.changeCountry === "18ebb77f-2341-e911-a85e-000d3ae0eb6e" ||
      !data.changeCountry;

    let nameChangeSourceCode;

    if (data.reason === "Marriage") {
      nameChangeSourceCode = isNZ ? 100000001 : 100000004;
    } else if (data.reason === "Civil union") {
      nameChangeSourceCode = isNZ ? 100000002 : 100000005;
    } else if (data.reason === "Statutory declaration / deed poll") {
      nameChangeSourceCode = isNZ ? 100000000 : 100000003;
    } else if (data.reason === "Changed to previous name") {
      // For this one, the country of birth (not change) is important to determine the sourcecode
      const bornInNZ = applicant?.countryOfBirthId === NZ_COUNTRY_ID;
      nameChangeSourceCode = bornInNZ ? 100000000 : 100000004;
    } else if (data.reason === "Other document") {
      nameChangeSourceCode = 100000006;
    }

    const getGenderEventTypeId = (greasonCode?: string | number) => {
      switch (Number(greasonCode)) {
        case 100000000:
          return DOC.NZ_TRAVEL_DOCUMENT; // NZ passport
        case 100000004:
          return DOC.NZ_CITIZENSHIP; // NZ citizenship
        case 100000001:
          return DOC.NZ_BIRTH; // NZ birth certificate
        case 100000002:
          return DOC.FOREIGN_PASSPORT; // Overseas passport
        case 100000003: // Other document
        default:
          return DOC.NAME_CHANGE_DOCUMENT;
      }
    };

    const genderActuallyChanged =
      editingGender &&
      !!data.newGender &&
      data.newGender !== (genderId ?? "") &&
      Number(data.greason) !== 100000003;
    // ---- Conditionally build eventTypeSearch ONLY for name changes ----
    const prevETS = appState.requestPayload.eventTypeSearch ?? [];
    console.log({ prevETS });
    const nextEventTypeSearch = (() => {
      if (!(editingName && data.reason)) {
        // If NOT a name change, don’t add anything. Keep whatever was there.
        return prevETS;
      }
      const eventTypeId = getEventTypeId(
        data.reason,
        isNZ,
        applicant?.countryOfBirthId
      );

      // For name change to previous name, countryofevent can't be set so needs to be determined here.
      const countryOfEventId = data.changeCountry
        ? data.changeCountry
        : data.reason === "Changed to previous name"
        ? applicant?.countryOfBirthId || NZ_COUNTRY_ID
        : NZ_COUNTRY_ID;

      // Build the name-change event row
      const nameChangeRow = {
        eventTypeId: eventTypeId,
        dateOfEvent: data.changeDate || "",
        countryOfEventId: countryOfEventId,
        // Uses ?? as we want to allow empty-string overwrites
        givenNames: toTitleCase(
          data.newGiven ?? applicant?.givenNames ?? ""
        ).slice(0, 100),
        familyName: toTitleCase(
          data.newFamily || applicant?.familyName || ""
        ).slice(0, 100),
        dateOfBirth: applicant?.dateOfBirth || "",
        countryOfBirthId: applicant?.countryOfBirthId || "",
        genderId: data.newGender || appState.requestPayload.applicant.genderId,
      };

      // If you want to keep index 0 (e.g., birth record) and add this as a second item:
      console.log(prevETS.length);
      if (prevETS.length) return [prevETS[0], nameChangeRow];

      // Otherwise just return this single row
      return [nameChangeRow];
    })();

    const GENDER_EVENT_TYPES = new Set<string>([
      DOC.NZ_TRAVEL_DOCUMENT, // NZ passport
      DOC.NZ_CITIZENSHIP, // NZ citizenship
      DOC.NZ_BIRTH, // NZ birth certificate
      DOC.FOREIGN_PASSPORT, // Overseas passport
      DOC.NAME_CHANGE_DOCUMENT, // "Other document" fallback
    ]);

    const buildGenderRow = () => ({
      eventTypeId: getGenderEventTypeId(data.greason),
      // If you capture a date/country for gender change, set them; else leave blank or NZ if that’s your policy.
      dateOfEvent: "",
      countryOfEventId: NZ_COUNTRY_ID,
      // Uses ?? as we want to allow empty-string overwrites
      givenNames: toTitleCase(
        data.newGiven ?? applicant?.givenNames ?? ""
      ).slice(0, 100),
      familyName: toTitleCase(
        data.newFamily || applicant?.familyName || ""
      ).slice(0, 100),
      dateOfBirth: applicant?.dateOfBirth || "",
      countryOfBirthId: applicant?.countryOfBirthId || "",
      genderId: data.newGender,
    });

    const upsertGenderEvent = (
      ets: Array<any>,
      shouldHaveGenderRow: boolean
    ): Array<any> => {
      const idx = ets.findIndex((row) =>
        GENDER_EVENT_TYPES.has(row.eventTypeId)
      );

      // if (!shouldHaveGenderRow) {
      //   // remove existing gender row if present
      //   return idx >= 0 ? ets.filter((_, i) => i !== idx) : ets;
      // }

      const genderRow = buildGenderRow();

      // if (idx >= 0) {
      //   // replace in place
      //   const copy = ets.slice();
      //   copy[idx] = { ...ets[idx], ...genderRow };
      //   return copy;
      // }
      // append
      if (shouldHaveGenderRow) return [...ets, genderRow];
      else return ets;
    };

    const nextEventTypeSearchWithGender = upsertGenderEvent(
      nextEventTypeSearch,
      genderActuallyChanged // boolean you already compute
    );

    let updatedAppState = {
      ...appState,
      requestPayload: {
        ...appState.requestPayload,
        applicant: {
          ...appState.requestPayload.applicant,
          // Uses ?? as we want to allow empty-string overwrites
          givenNames: toTitleCase(
            data.marriageGiven ?? data.newGiven ?? applicant?.givenNames ?? ""
          ),
          familyName: toTitleCase(
            data.marriageFamily || data.newFamily || applicant?.familyName || ""
          ),
          genderId:
            data.newGender ?? appState.requestPayload.applicant.genderId,
        },
        supportingInformation: {
          ...appState.requestPayload.supportingInformation,
          ...(data.greason && { genderSourceCode: Number(data.greason) }),
          ...(nameChangeSourceCode && { nameChangeSourceCode }),
        },
        eventTypeSearch: nextEventTypeSearchWithGender,
      },
    };

    if (updatedAppState.requestPayload.applicationId) {
      updateApplication(updatedAppState.requestPayload)
        .then(() => {
          setEditingName(false);
          setEditingGender(false);
          setAppState(updatedAppState);
          handleNext(); // or save to context here
        })
        .catch((err) => {
          console.error("Failed to update application", err);
          setLoading(false);
        });
    } else {
      updatedAppState = {
        ...updatedAppState,
        requestPayload: {
          ...updatedAppState.requestPayload,
          applicationFlowCode: data.greason ? 100000004 : 100000002,
          applicationTypeId: (calculateAge(applicant?.dateOfBirth || "") < 14) ? IVS_CHILD : IVS_ADULT,
        },
      };
      initiateApplication(updatedAppState.requestPayload)
        .then((response) => {
          updatedAppState.requestPayload.applicationId = response.serviceId;

          // setEditingName(false);
          // setEditingGender(false);
          setUserFlows((prev: any) => ({
            ...prev,
            isChangeRequested: true,
            isCancelRequested: true,
            showRefree: prev.isRenew ? false : prev.showRefree,
          }));
          submitApplication({
            serviceId: response.serviceId,
          })
            .then(() => {
              setLoading(false);
              setAppState(updatedAppState);
              handleNext();
            })
            .catch((err) => {
              console.error("Failed to submit application", err);
              setLoading(false);
            });

          // or save to context here
        })
        .catch((err) => {
          console.error("Failed to initiate application", err);
          setLoading(false);
        });
    }
  };

  /* ---------- render ----------------- */
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {loading && <Loader />}

      {/* -------- INTRO -------- */}
      <div className="intro no-min-height request-change-container">
        <h1 className="intro-title-mi" lang="mi-NZ">
          Kōrero mai mēnā he whakahoutanga anō
        </h1>
        <hr />
        <h1 className="intro-title">Tell us about any changes</h1>

        <div className="step-container">
          {!editingGender && !editingName && (
            <h2 className="step-title" style={{ marginBottom: 40 }}>
              Are the following details correct?
            </h2>
          )}
          {(editingGender || editingName) && (
            <h2 className="step-title">Your name details</h2>
          )}
          {/* -------- NAME ROW -------- */}
          <div className="step-container-box" style={{ marginBottom: 16 }}>
            <p className="label">Full name</p>
            <p className="value">{fullName}</p>
          </div>

          {!editingName ? (
            <EditButton
              text="I've changed my name"
              onClick={() => setEditingName(true)}
            />
          ) : (
            <>
              <CancelButton
                text="Cancel name change"
                onClick={() => setEditingName(false)}
              />
              {(reasonSelected === "Marriage" ||
                reasonSelected === "Civil union") && (
                <React.Fragment>
                  <h2 className="step-title">Your new name details</h2>
                  <p className="mb-16 mt-0">
                    This is the name we will use for your verified identity.
                  </p>
                  <div
                    className={`floating-field field-wrapper mb-16 ${
                      errors.marriageGiven ? "error-field" : ""
                    }`}
                  >
                    <input
                      id="marriageGiven"
                      type="text"
                      placeholder=" "
                      className="common-field"
                      aria-label="New name - given name(s)"
                      {...register("marriageGiven", {
                        validate: (value) => {
                          return (
                            value?.trim().length < 198 ||
                            "Your given name must be fewer than 198 characters."
                          );
                        },
                      })}
                    />
                    <label htmlFor="marriageGiven" className="field-label">
                      Given name(s)
                    </label>
                  </div>
                  <FieldError
                    error={errors.marriageGiven}
                    info={null}
                    id="marriageGiven"
                  />

                  <div
                    className={`floating-field field-wrapper mb-16 ${
                      errors.marriageFamily ? "error-field" : ""
                    }`}
                  >
                    <input
                      id="marriageFamily"
                      type="text"
                      placeholder=" "
                      className="common-field"
                      aria-label="New name - surname or family name"
                      {...register("marriageFamily", {
                        required:
                          "If you are entering one name, please enter it in surname or family name.",
                        maxLength: {
                          value: 84,
                          message:
                            "Your surname or family name must be fewer than 85 characters.",
                        },
                      })}
                    />
                    <label
                      htmlFor="marriageFamily"
                      className="field-label"
                      role="presentation"
                    >
                      Surname or family name
                      <span className="req-text" aria-label="required">
                        *
                      </span>
                    </label>
                  </div>
                  <FieldError
                    error={errors.marriageFamily}
                    info={null}
                    id="marriageFamily"
                  />
                  {(containsSpecialCharsMarriage ||
                    containsSpecialCharsMarriageGivenName) && (
                    <FieldError
                      error={null}
                      info="Your name contains a special character (such as an umlaut, cedilla, accent, or macron). When your name is recorded by other organisations that use RealMe, it may appear differently."
                      id="newFamily"
                    />
                  )}
                </React.Fragment>
              )}

              <h2 className="step-title">Reason for name change</h2>

              <FloatingSelectField
                name="reason"
                label="Select why your name has changed"
                register={register}
                required
                value={reasonSelected} // watch("reason")
                options={[
                  { value: "Marriage", label: "Marriage" },
                  { value: "Civil union", label: "Civil union" },
                  {
                    value: "Statutory declaration / deed poll",
                    label: "Statutory declaration / deed poll",
                  },
                  {
                    value: "Changed to previous name",
                    label: "Changed to previous name",
                  },
                  { value: "Other document", label: "Other document" },
                ]}
                error={errors.reason}
              />

              <p className="mb-16 mt-0">
                Enter your details exactly as they appear on the document. For marriage and civil union certificates, this will mean entering your previous name.
              </p>

              <div
                className={`floating-field field-wrapper mb-16 ${
                  errors.newGiven ? "error-field" : ""
                }`}
              >
                <input
                  id="newGiven"
                  type="text"
                  placeholder=" "
                  className="common-field"
                  aria-label="Name on document - given name(s)"
                  {...register("newGiven", {
                    validate: (value) => {
                      return (
                        value?.trim().length < 198 ||
                        "Your given name must be fewer than 198 characters."
                      );
                    },
                  })}
                />
                <label htmlFor="newGiven" className="field-label">
                  Given name(s)
                </label>
              </div>
              <FieldError error={errors.newGiven} info={null} id="newGiven" />

              <div
                className={`floating-field field-wrapper mb-16 ${
                  errors.newFamily ? "error-field" : ""
                }`}
              >
                <input
                  id="newFamily"
                  type="text"
                  placeholder=" "
                  className="common-field"
                  aria-label="Name on document - surname or family name"
                  {...register("newFamily", {
                    required:
                      "If you are entering one name, please enter it in surname or family name.",
                    maxLength: {
                      value: 84,
                      message:
                        "Your surname or family name must be fewer than 85 characters.",
                    },
                  })}
                />
                <label
                  htmlFor="newFamily"
                  className="field-label"
                  role="presentation"
                >
                  Surname or family name
                  <span className="req-text" aria-label="required">
                    *
                  </span>
                </label>
              </div>
              <FieldError error={errors.newFamily} info={null} id="newFamily" />
              {/* Diacritics accepted if reason for change is marriage or civil union */}
              {reasonSelected !== "Marriage" &&
                reasonSelected !== "Civil union" &&
                (containsSpecialChars || containsSpecialCharsGivenName) && (
                  <FieldError
                    error={null}
                    info="Your name contains a special character (such as an umlaut, cedilla, accent, or macron). When your name is recorded by other organisations that use RealMe, it may appear differently."
                    id="newFamily"
                  />
                )}
              {showChangeDate && (
                <FloatingDateField
                  name="changeDate"
                  label="Date of name change (dd/mm/yyyy)"
                  control={control}
                  error={errors.changeDate}
                  calendarIcon={Calendar}
                  required={reasonSelected !== "Changed to previous name"}
                  maxDate={new Date()}
                />
              )}

              {showChangeCountry && (
                <FloatingSelectField
                  name="changeCountry"
                  label="Country where name change was recorded"
                  register={register}
                  required={reasonSelected !== "Changed to previous name"}
                  value={countrySelected}
                  options={countries.map((c) => ({
                    value: c.dia_countryid,
                    label: c.Name,
                  }))}
                  error={errors.changeCountry}
                />
              )}

              {!showContinue && !userFlows.isRenew && isOverseas && (
                <FieldError
                  error={null}
                  info="We'll need to see a document where this name is recorded. <br /> Continue this application online, then follow the instructions to visit a RealMe partner store <br /> with the required documents."
                />
              )}

              {(showContinue || userFlows.isRenew) && isOverseas && (
                <FieldError
                  error={null}
                  info="We will need to see this document. We will contact you with instructions."
                />
              )}
            </>
          )}

          {!(editingGender || editingName) && <div style={{ marginTop: 40 }} />}
          {(editingGender || editingName) && <div className="spacer-64" />}
          {(editingGender || editingName) && (
            <h2 className="step-title" style={{ marginBottom: 16 }}>
              Your gender details
            </h2>
          )}

          {/* -------- GENDER ROW -------- */}
          <div className="step-container-box" style={{ marginBottom: 16 }}>
            <p className="label">Gender</p>
            <p className="value">{genderLabel}</p>
          </div>

          {!editingGender ? (
            <EditButton
              text="I've changed my gender"
              onClick={() => setEditingGender(true)}
            />
          ) : (
            <>
              <CancelButton
                text="Cancel gender change"
                onClick={() => setEditingGender(false)}
              />

              {/* gender-edit fields */}
              <div style={{ marginTop: 40 }} />
              {editingGender && (
                <p className="gender-change-note">
                  We need to know where your gender change is recorded so we can
                  match your gender to another official record.
                </p>
              )}

              <FloatingSelectField
                name="newGender"
                label="Select your current gender"
                register={register}
                required
                value={newGenderValue}
                options={[
                  {
                    value: "dcd24bf2-7a1f-e911-a849-000d3ae0b82e",
                    label: "Male",
                  },
                  {
                    value: "c77f9cf8-7a1f-e911-a849-000d3ae0b82e",
                    label: "Female",
                  },
                  {
                    value: "353656ca-3820-e911-a853-000d3ae0a6cf",
                    label: "Non-binary",
                  },
                ]}
                error={errors.newGender}
              />

              {newGenderValue === "353656ca-3820-e911-a853-000d3ae0a6cf" && (
                <FieldError
                  error={null}
                  info="Other agencies that use RealMe may record your gender as Other, due to system conditions."
                />
              )}

              <FloatingSelectField
                name="greason"
                label="Document where gender change is recorded"
                register={register}
                required
                value={greasonSelected}
                options={[
                  { value: 100000000, label: "NZ passport" },
                  { value: 100000004, label: "NZ citizenship" },
                  { value: 100000001, label: "NZ birth certificate" },
                  { value: 100000002, label: "Overseas passport" },
                  { value: 100000003, label: "Other document" },
                ]}
                error={errors.greason}
              />

              {!showContinue &&
                !userFlows.isRenew &&
                (greasonSelected === "100000003" ||
                  greasonSelected === "100000002") && (
                  <FieldError
                    error={null}
                    info="We will need to see this document. Continue this application online, then follow the instructions to visit a RealMe partner store with the required documents."
                  />
                )}

              {(showContinue || userFlows.isRenew) &&
                (greasonSelected === "100000003" ||
                  greasonSelected === "100000002") && (
                  <FieldError
                    error={null}
                    info="We will need to see this document. We will contact you with instructions."
                  />
                )}
            </>
          )}
        </div>
      </div>
      {/* {!(editingGender || editingName) && <div style={{ height: 24 }} />} */}
      {/* -------- FOOTER -------- */}

      <div className="step-button-container">
        {!showContinue ? (
          <SubmitButton
            text={
              editingGender || editingName ? "Save and continue" : "Continue"
            }
            type="submit"
            disabled={(editingGender || editingName) && !isValid}
            onClick={() => {}}
          />
        ) : (
          <SubmitButton
            text={editingGender || editingName ? "Submit" : "Submit"}
            type="submit"
            disabled={(editingGender || editingName) && !isValid}
            onClick={() => {}}
          />
        )}

        <BackButton text="Back" onClick={handleBack} />
      </div>
    </form>
  );
}
